import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Label";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { Copy, RefreshCw, ExternalLink, Settings } from "lucide-react";
import { copyToClipboard } from "@/lib/utils";

interface ProviderPreset {
  name: string;
  authEndpoint: string;
  defaultScopes: string[];
  scopeDelimiter: string;
}

const PROVIDERS: Record<string, ProviderPreset> = {
  google: {
    name: "Google",
    authEndpoint: "https://accounts.google.com/o/oauth2/v2/auth",
    defaultScopes: ["openid", "https://www.googleapis.com/auth/userinfo.profile", "https://www.googleapis.com/auth/userinfo.email"],
    scopeDelimiter: " "
  },
  github: {
    name: "GitHub",
    authEndpoint: "https://github.com/login/oauth/authorize",
    defaultScopes: ["user:email", "read:user", "repo"],
    scopeDelimiter: " "
  },
  microsoft: {
    name: "Microsoft",
    authEndpoint: "https://login.microsoftonline.com/common/oauth2/v2.0/authorize",
    defaultScopes: ["openid", "profile", "offline_access", "User.Read"],
    scopeDelimiter: " "
  },
  discord: {
    name: "Discord",
    authEndpoint: "https://discord.com/api/oauth2/authorize",
    defaultScopes: ["identify", "email", "guilds"],
    scopeDelimiter: " "
  },
  auth0: {
    name: "Auth0",
    authEndpoint: "https://YOUR_DOMAIN.auth0.com/authorize",
    defaultScopes: ["openid", "profile", "email"],
    scopeDelimiter: " "
  },
  custom: {
    name: "Custom Provider",
    authEndpoint: "https://example.com/oauth/authorize",
    defaultScopes: ["read", "write"],
    scopeDelimiter: " "
  }
};

export function OAuthUrlGenerator() {
  const [providerKey, setProviderKey] = useState("google");
  const [authEndpoint, setAuthEndpoint] = useState(PROVIDERS.google.authEndpoint);
  const [clientId, setClientId] = useState("your-client-id-here");
  const [redirectUri, setRedirectUri] = useState("https://your-app.com/callback");
  const [state, setState] = useState("");
  const [responseType, setResponseType] = useState("code");
  const [selectedScopes, setSelectedScopes] = useState<string[]>(PROVIDERS.google.defaultScopes);
  const [customScope, setCustomScope] = useState("");
  const [extraParams, setExtraParams] = useState<Array<{ key: string; value: string }>>([]);
  const [newParamKey, setNewParamKey] = useState("");
  const [newParamValue, setNewParamValue] = useState("");
  const [oauthUrl, setOauthUrl] = useState("");
  const [copied, setCopied] = useState(false);

  // Generate random safe state string
  const generateRandomState = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < 16; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setState(result);
  };

  useEffect(() => {
    generateRandomState();
  }, []);

  // Sync endpoint and scopes when provider preset changes
  const handleProviderChange = (key: string) => {
    setProviderKey(key);
    const preset = PROVIDERS[key];
    setAuthEndpoint(preset.authEndpoint);
    setSelectedScopes(preset.defaultScopes);
  };

  // Compile URL
  useEffect(() => {
    try {
      const url = new URL(authEndpoint);
      url.searchParams.set("client_id", clientId);
      url.searchParams.set("redirect_uri", redirectUri);
      
      const delimiter = PROVIDERS[providerKey]?.scopeDelimiter || " ";
      url.searchParams.set("scope", selectedScopes.join(delimiter));
      
      url.searchParams.set("response_type", responseType);
      
      if (state) {
        url.searchParams.set("state", state);
      }

      // Append additional params
      extraParams.forEach(param => {
        if (param.key && param.value) {
          url.searchParams.set(param.key, param.value);
        }
      });

      setOauthUrl(url.toString());
    } catch {
      setOauthUrl("Error: Invalid Authorization Endpoint URL format.");
    }
  }, [authEndpoint, clientId, redirectUri, state, responseType, selectedScopes, extraParams, providerKey]);

  const handleCopy = () => {
    if (!oauthUrl || oauthUrl.startsWith("Error")) return;
    copyToClipboard(oauthUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleToggleScope = (scope: string) => {
    if (selectedScopes.includes(scope)) {
      setSelectedScopes(selectedScopes.filter(s => s !== scope));
    } else {
      setSelectedScopes([...selectedScopes, scope]);
    }
  };

  const handleAddCustomScope = (e: React.FormEvent) => {
    e.preventDefault();
    if (customScope && !selectedScopes.includes(customScope)) {
      setSelectedScopes([...selectedScopes, customScope]);
      setCustomScope("");
    }
  };

  const handleAddParam = (e: React.FormEvent) => {
    e.preventDefault();
    if (newParamKey && newParamValue) {
      setExtraParams([...extraParams, { key: newParamKey, value: newParamValue }]);
      setNewParamKey("");
      setNewParamValue("");
    }
  };

  const handleRemoveParam = (index: number) => {
    setExtraParams(extraParams.filter((_, i) => i !== index));
  };

  return (
    <ToolPageLayout
      title="OAuth URL Generator - Build Authorization URLs"
      description="Easily generate and validate custom OAuth 2.0 authorization request URLs for Google, GitHub, Microsoft, Discord, Auth0, or custom ID providers."
      h1="OAuth URL Generator"
      category="JWT & Security"
      categoryPath="/jwt-security"
      howToUse={[
        "Choose an OAuth provider preset or select 'Custom' to use custom settings.",
        "Populate your unique credentials: Client ID, Redirect Callback URI, and Response Type.",
        "Toggle default scope checkboxes or add custom scopes.",
        "Generate a cryptographically safe state parameter to shield against CSRF exploits.",
        "Copy or test the complete compiled OAuth 2.0 URL instantly."
      ]}
      faq={[
        { question: "What is an online OAuth URL generator and why do I need one?", answer: "An online OAuth URL generator is a utility that automates compiling clean, correctly formatted, and encoded OAuth 2.0 authorization endpoints. Developers use it to avoid manual syntax errors with scopes, redirection URIs, state values, and other URL query parameters." },
        { question: "How does the OAuth 2.0 authorization code flow work?", answer: "The Authorization Code Flow redirects a user to an identity provider's consent page. Upon login approval, the provider redirects back to your callback URI with a temporary authorization code. Your backend server then exchanges this code for a secure, long-lived access token." },
        { question: "What is the 'client_id' parameter in an OAuth URL?", answer: "The 'client_id' is a public identifier generated by your identity provider (like Google or GitHub Console) when registering your application. It informs the authorization server which specific application is initiating the request." },
        { question: "Why is the 'redirect_uri' (callback URL) strictly validated?", answer: "The redirect URI is the endpoint where authorization codes or tokens are returned. Providers enforce an exact, strict character match against pre-registered URIs to prevent malicious attackers from hijacking codes by redirecting them to unauthorized domains." },
        { question: "Why is the OAuth 2.0 'state' parameter critical for web security?", answer: "The 'state' parameter acts as a high-entropy anti-CSRF (Cross-Site Request Forgery) token. Your application generates a random state value, sends it with the request, and verifies that the returning state payload matches exactly before processing redirects." },
        { question: "What is the difference between response_type='code' and response_type='token'?", answer: "'response_type=code' initiates the modern, secure Authorization Code Flow (where authorization is traded server-side). 'response_type=token' initiates the legacy, vulnerable Implicit Flow, which has been deprecated due to the risks of exposing access tokens directly in front-end browser histories." },
        { question: "How do I format OAuth scopes when constructing custom endpoints?", answer: "OAuth scopes are string permissions (e.g., 'read:user' or 'openid email'). When building URLs, these scopes must be combined and separated by a standard space character. This tool automatically joins and URL-encodes spaces as '%20' for safe transport." },
        { question: "Does this OAuth URL generator support PKCE (Proof Key for Code Exchange)?", answer: "Yes. By utilizing the custom parameters or integrating with PKCE challenges, you can append 'code_challenge' and 'code_challenge_method=S256' to authorize requests, protecting mobile or single-page applications from interception attacks." },
        { question: "Can I pass custom query variables through OAuth redirection flow?", answer: "Yes, you can append custom queries either by appending them to your 'redirect_uri' (if permitted by the provider) or by packing them into the 'state' parameter as a JSON object, or by using provider-specific options like Google's 'state' and 'login_hint' attributes." },
        { question: "What is the difference between OAuth 2.0 and OpenID Connect (OIDC)?", answer: "OAuth 2.0 is designed strictly for access delegation (authorization), granting a client app limited access to APIs. OpenID Connect is a simple identity layer built on top of OAuth 2.0, providing authentication (identity claims, like user profile and email, typically wrapped inside an ID Token)." }
      ]}
    >
      <div className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Settings Console */}
          <div className="space-y-4 bg-white border border-slate-200 p-5 rounded-2xl shadow-xs">
            <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-100 text-rose-600">
              <Settings className="w-5 h-5" />
              <span className="font-bold text-sm uppercase tracking-wider">OAuth Configuration</span>
            </div>

            <div>
              <Label className="text-sm font-semibold text-slate-700">Select Provider Preset</Label>
              <div className="grid grid-cols-3 gap-1.5 mt-1">
                {Object.entries(PROVIDERS).map(([key, provider]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handleProviderChange(key)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all ${
                      providerKey === key
                        ? "bg-rose-50 border-rose-200 text-rose-700 font-bold"
                        : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {provider.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold text-slate-700">Authorization Endpoint</Label>
              <Input
                type="text"
                value={authEndpoint}
                onChange={(e) => setAuthEndpoint(e.target.value)}
                className="mt-1 font-mono text-xs"
                placeholder="https://provider.com/oauth/authorize"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-semibold text-slate-700">Client ID</Label>
                <Input
                  type="text"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  className="mt-1 text-xs font-mono"
                  placeholder="Client ID..."
                />
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700">Response Type</Label>
                <select
                  value={responseType}
                  onChange={(e) => setResponseType(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-xs mt-1"
                >
                  <option value="code">code (Auth Code Flow)</option>
                  <option value="token">token (Implicit Flow)</option>
                  <option value="code id_token">code id_token (Hybrid)</option>
                  <option value="id_token">id_token (OpenID Connect)</option>
                </select>
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold text-slate-700">Redirect URI</Label>
              <Input
                type="text"
                value={redirectUri}
                onChange={(e) => setRedirectUri(e.target.value)}
                className="mt-1 text-xs font-mono"
                placeholder="https://your-domain.com/callback"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <Label className="text-sm font-semibold text-slate-700">State (CSRF Protection)</Label>
                <button
                  onClick={generateRandomState}
                  className="text-xs text-rose-600 hover:text-rose-700 flex items-center gap-1"
                >
                  <RefreshCw className="w-3 h-3" /> Regenerate
                </button>
              </div>
              <Input
                type="text"
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="text-xs font-mono"
                placeholder="Random state string..."
              />
            </div>

            {/* Scope Selection */}
            <div>
              <Label className="text-sm font-semibold text-slate-700">Select Scopes</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {PROVIDERS[providerKey]?.defaultScopes.map((scope) => (
                  <button
                    key={scope}
                    type="button"
                    onClick={() => handleToggleScope(scope)}
                    className={`px-2 py-1 text-[11px] rounded-md border font-mono transition-colors ${
                      selectedScopes.includes(scope)
                        ? "bg-rose-100 border-rose-200 text-rose-800 font-medium"
                        : "bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100"
                    }`}
                  >
                    {scope}
                  </button>
                ))}
              </div>

              {/* Add Custom Scope Form */}
              <form onSubmit={handleAddCustomScope} className="flex gap-2 mt-2">
                <Input
                  type="text"
                  placeholder="Add custom scope (e.g. read:profile)"
                  value={customScope}
                  onChange={(e) => setCustomScope(e.target.value)}
                  className="text-xs h-8"
                />
                <Button type="submit" size="sm" variant="outline" className="h-8 text-xs">
                  Add
                </Button>
              </form>
            </div>
          </div>

          {/* Target Output Box */}
          <div className="flex flex-col justify-between space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-4 flex-1">
              <div className="flex items-center justify-between">
                <span className="font-bold text-sm uppercase tracking-wider text-rose-600">Generated URL Parameters</span>
                <span className="text-xs font-mono text-slate-400">Live Compilation</span>
              </div>

              <div className="space-y-3">
                <div className="p-4 bg-white border border-slate-200 rounded-xl font-mono text-xs select-all break-all leading-relaxed text-slate-800 min-h-[140px]">
                  {oauthUrl}
                </div>

                <div className="space-y-1 bg-white border border-slate-100 rounded-xl p-3 text-xs text-slate-600">
                  <p className="font-semibold text-slate-700 mb-1">Decoded Query Fields:</p>
                  <p>🔹 <strong className="font-medium">client_id:</strong> <span className="font-mono text-slate-500">{clientId}</span></p>
                  <p>🔹 <strong className="font-medium">redirect_uri:</strong> <span className="font-mono text-slate-500">{redirectUri}</span></p>
                  <p>🔹 <strong className="font-medium">scope:</strong> <span className="font-mono text-slate-500">{selectedScopes.join(", ") || "none"}</span></p>
                  <p>🔹 <strong className="font-medium">response_type:</strong> <span className="font-mono text-slate-500">{responseType}</span></p>
                  {state && <p>🔹 <strong className="font-medium">state:</strong> <span className="font-mono text-slate-500">{state}</span></p>}
                </div>
              </div>
            </div>

            {/* Quick Action Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={handleCopy}
                disabled={oauthUrl.startsWith("Error")}
                className="flex-1 bg-slate-900 hover:bg-slate-800 text-white gap-2"
              >
                <Copy className="w-4 h-4" />
                {copied ? "Copied!" : "Copy OAuth URL"}
              </Button>
              {!oauthUrl.startsWith("Error") && (
                <a
                  href={oauthUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-semibold text-sm px-4 rounded-md gap-1.5 transition-colors"
                >
                  Test URL <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* OAuth 2.0 visual guide */}
        <div className="bg-slate-50 border border-slate-100 rounded-2xl p-5">
          <h4 className="font-bold text-sm uppercase tracking-wider text-slate-800 mb-3">Understanding the OAuth 2.0 Authorization Flow</h4>
          <div className="grid md:grid-cols-3 gap-4 text-xs text-slate-600">
            <div className="p-4 bg-white rounded-xl border border-slate-200 shadow-3xs space-y-1">
              <span className="font-bold text-rose-600">1. Client Request</span>
              <p className="leading-relaxed">Your application redirects the user's browser to the generated URL. The user logs in and consents to specified scopes.</p>
            </div>
            <div className="p-4 bg-white rounded-xl border border-slate-200 shadow-3xs space-y-1">
              <span className="font-bold text-rose-600">2. Authorization Code</span>
              <p className="leading-relaxed">The identity provider redirects the user back to your <code className="bg-slate-100 p-0.5 rounded">Redirect URI</code> with an authorization code parameter in the query.</p>
            </div>
            <div className="p-4 bg-white rounded-xl border border-slate-200 shadow-3xs space-y-1">
              <span className="font-bold text-rose-600">3. Token Exchange</span>
              <p className="leading-relaxed">Your backend service sends a secure request containing the code and Client Secret to get the access token and query user data.</p>
            </div>
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}
